import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { DomSanitizer } from "@angular/platform-browser";
import { HttpErrorResponse } from '@angular/common/http';
import { RequestParamModel } from '../../models/request-params.model';
import { BackendUserService } from '../../apiservices/backend-user.service';
import { TasksService } from '../../apiservices/tasks.service';
import { AlertService } from '../../services/alert.service';
import { Constants } from '../../constants';
import { HelperService } from '../../services/helpers/helper.service';
import { ContactsService } from '@coreapiservices';
import { SubscribersService } from '../../services/subscribers/subscribers.service';
@Component({
  standalone: false,
  selector: 'app-confirm-modal',
  // Eager: ngx-bootstrap's BsModalService renders this component once inside show(), BEFORE
  // the caller assigns .content.title / .message / .taskId / .displayTaskFields. Under this
  // Angular version's OnPush-by-default, that first paint is empty and the view is never
  // marked dirty again, so the dialog showed only the Cancel/Confirm buttons (no title,
  // message or task options). Eager (CheckAlways) lets the next tick paint the assigned
  // inputs — the pre-OnPush behaviour every caller was written against.
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './confirm-modal.component.html',
  styleUrls: ['./confirm-modal.component.css', '../../../app.component.css']
})
export class ConfirmModalComponent implements OnInit {

  /* ################### Variable Declaration Start ################### */
  public onClose!: Subject<boolean | {}>;
  modalRef!: BsModalRef;
  @Input() message!: any;
  @Input() note!: any;
  @Input() title!: any;
  @Input() taskId!: any;
  @Input() assigned_to!: any;
  @Input() displayTaskFields!: any
  @Input() contactId!: any;
  @Input() taskCategoryId!: any;
  @Input() showSrdWarning: boolean = false;
  @Input() invalidSku: boolean = false;
  @Input() isTenderSelected: boolean = false;
  @Input() isDisplayAddProductBySkuPopup: boolean = false;
  @Input() isDraftConfirmationPopup: boolean = false;
  /** The confirm button's text in the standard Cancel / Confirm footer. */
  @Input() confirmLabel: string = 'Confirm';
  @Input() currentSampleStatus !: any
  // The latest task object. Set imperatively after modalService.show(); the setter (not
  // ngOnInit) runs the pre-fill so it never reads stale/undefined inputs.
  @Input() set currentTask(task: any) {
    this.prefillFromTask(task);
  }
  @Output() onCancel = new EventEmitter();
  @Output() onConfirm = new EventEmitter();
  isCheckTask: string = 'complete';
  assignToUserList = [];
  showAssignedUserList: boolean = false;
  showRescheduleDatePicker: boolean = false;
  scheduledDate: Date | null = null;
  minScheduledDate: Date = new Date();
  scheduledDateError: boolean = false;
  taskNote: string = '';
  taskNoteError: boolean = false;
  isLoading = false;
  isClientResponded: boolean = true;
  isSydneyMetroAddressChecked: boolean = false;
  constants = Constants;
  overChargeAction: 'refund' | 'deposit-credit' = 'refund';
  refundInstruction: any;
  /* ################### Variable Declaration End ################### */

  /* ################### constructor Declaration Start ################### */
  constructor(private _bsModalRef: BsModalRef, public sanitizer: DomSanitizer,
    private backendService: BackendUserService,
    private taskService: TasksService,
    private alertService: AlertService,
    private changeDetection: ChangeDetectorRef,
    private helperService: HelperService,
    private subscribeService: SubscribersService
  ) {
  }

  /* ################### constructor Declaration End ################### */

  /************************************
   * Life cycle hooks start
   ************************************/

  ngOnInit(): void {
    this.onClose = new Subject();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.minScheduledDate = tomorrow;
  }

  /**
   * Pre-fills the modal from the latest task object so it always reflects the current
   * status, scheduled date and note. Invoked from the `currentTask` input setter (after
   * the modal inputs are assigned), not ngOnInit, so it never reads stale/undefined values.
   * A task counts as rescheduled when its status is 'R', or it carries a scheduled date or
   * a note (some backends keep the status 'P' but store the reschedule note/date).
   */
  private prefillFromTask(task: any): void {
    if (!task) {
      return;
    }
    const note = (typeof task.note === 'string' && task.note.trim().length) ? task.note.trim() : null;
    const isCompleted = task.status === Constants.TASK_COMPLETED;
    const wasRescheduled = task.status === Constants.TASK_RESCHEDULED || !!task.scheduled_date || !!note;
    if (wasRescheduled && !isCompleted) {
      this.isCheckTask = Constants.RESCHEDULE;
      this.showRescheduleDatePicker = true;
      this.showAssignedUserList = false;
      if (task.scheduled_date) {
        const parsed = new Date(task.scheduled_date);
        if (!isNaN(parsed.getTime())) {
          this.scheduledDate = parsed;
        }
      }
      if (note) {
        this.taskNote = note;
      }
    }
  }

  /************************************
   * Life cycle hooks end
   ************************************/

  /************************************
   * Other functions start
   ************************************/

  /**
   * Rescheduling a container's Add GPSM Reference task means production is running late, so it
   * needs a note: the reason sales see in the container's delay history. api-v2 enforces the same.
   */
  get isTaskNoteRequired(): boolean {
    return this.isCheckTask === this.constants.RESCHEDULE
      && this.taskCategoryId == this.constants.TASK_CATEGORIES.ORDER_ADD_GPSM_REFERENCE;
  }

  confirm(): void {
    if (this.isCheckTask === this.constants.RE_ASSIGN_USER) {
      this.updateAssignToUserList();
    } else if (this.isCheckTask === this.constants.RESCHEDULE) {
      // Optional note — only forwarded to the backend when a value is provided.
      const note = this.taskNote && this.taskNote.trim() ? this.taskNote.trim() : null;
      this.scheduledDateError = !this.scheduledDate;
      this.taskNoteError = this.isTaskNoteRequired && !note;
      if (this.scheduledDateError || this.taskNoteError) {
        return;
      }
      const reschedulePayload: any = { action: this.constants.RESCHEDULE, scheduledDate: this.scheduledDate };
      if (note) {
        reschedulePayload.note = note;
      }
      this.onClose.next(reschedulePayload);
      this._bsModalRef.hide();
      this.onConfirm.emit();
    } else {
      this.subscribeService.emitClientResponse(this.isClientResponded);
      this.subscribeService.emitHandDeliverySampleResponse(this.isSydneyMetroAddressChecked);

      if (this.taskCategoryId == this.constants.TASK_CATEGORIES.OTHER_OVERPAYMENT_RECEIVED) {
        this.onClose.next({overChargeAction: this.overChargeAction, instruction: this.refundInstruction});
      } else {
        this.onClose.next(true);
      }
      this._bsModalRef.hide();
      this.onConfirm.emit();
    }
  }

  decline(): void {
    this.onClose.next(false);
    this._bsModalRef.hide();
    this.onCancel.emit();
  }

  showAssignToUserList(event) {
    this.isCheckTask = event.target.value;
    this.scheduledDateError = false;
    this.taskNoteError = false;
    this.changeDetection.detectChanges();
    if (event.target.value == Constants.RE_ASSIGN_USER) {
      this.getAssignedToUserList();
      this.showAssignedUserList = true;
      this.showRescheduleDatePicker = false;
    } else if (event.target.value == Constants.RESCHEDULE) {
      this.showAssignedUserList = false;
      this.showRescheduleDatePicker = true;
    } else {
      this.showAssignedUserList = false;
      this.showRescheduleDatePicker = false;
    }
  }

  getAssignedToUserList(): void {
    this.assignToUserList = [];
    let requestParamModel = new RequestParamModel();
    requestParamModel.pageSize = 'all';
    this.backendService.getBackEndUser(requestParamModel).subscribe((dataList: any) => {
      this.isLoading = false;
      requestParamModel.update<any>(dataList);
      this.assignToUserList = dataList.data.map(item => {
        return { id: item.id, name: `${item.first_name} ${item.last_name}` }
      })
    }, (error: HttpErrorResponse) => {
      this.isLoading = false;
    });
  }

  updateAssignToUserList() {
    let json = {
      assigned_to: this.assigned_to
    }
    this.isLoading = true;
    this.taskService.updateTask(json, this.taskId).subscribe((data) => {
      this.changeDetection.detectChanges();
      // this._bsModalRef.hide();
      this.onClose.next(false);
      this._bsModalRef.hide();
      this.onConfirm.emit();

      this.isLoading = false;
      this.alertService.success(Constants.STATUS_CHANGES_SUCCESS);
    }, (error: HttpErrorResponse) => {
      this.isLoading = false;
      this._bsModalRef.hide();
      this.helperService.handleError(error);
    })
  }

  toggleClientResponseCheckbox(event) {
    event?.target?.checked ? this.isClientResponded = true : this.isClientResponded = false;
  }

  onSalesRepCheckboxChange(event) {
    event?.target?.checked ? this.isSydneyMetroAddressChecked = true : this.isSydneyMetroAddressChecked = false;
  }
  closeInvalidSkuPopup() {
    this.onClose.next(true);
    this._bsModalRef.hide();
  }

  onOverchargeStatusChange(event: Event) {
    const value = (event.target as HTMLInputElement).value as 'refund' | 'deposit-credit';
    this.overChargeAction = value;
  }
}
